import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
    Checkbox,
    Form,
    FormActions,
    FormDropdown,
    FormFields,
    FormMultiselectDropdown,
    FormTextField,
    FormValidationSummary,
    mapToDropdownOption,
    useNotifications
} from "sprint-common-web";

import { ContentReferenceType } from "../../types/ContentReferenceType";
import { FormDistributorSearchDropdown } from "../form/FormDistributorDropdown";
import { ContentVersionDetails, CreateContentVersionParams } from "./ContentVersion";
import { useCreateContentVersionForm } from "./useCreateContentVersionForm";

export interface CreateContentVersionFormInputs {
    versionTypeId: string;
    distributorId: string;
    code: string;
    brandingIds: string[];
}

export interface CreateContentVersionFormProps {
    onCancel(): void;
    onSubmit(newVersion: ContentVersionDetails): void;
    content: CreateContentVersionParams;
}

export const CreateContentVersionForm: React.FunctionComponent<CreateContentVersionFormProps> = (
    props: CreateContentVersionFormProps
) => {
    const { success, error } = useNotifications();
    const { errors, control, formState, reset, setValue, handleSubmit, watch } = useForm<
        CreateContentVersionFormInputs
    >({
        defaultValues: {
            distributorId: "",
            versionTypeId: "",
            code: "",
            brandingIds: []
        },
        criteriaMode: "all"
    });

    const values = watch();

    const {
        versionTypes,
        airlines,
        createNewContentVersion,
        trailersOnly,
        setTrailersOnly,
        validationResult,
        setValidationResult
    } = useCreateContentVersionForm({
        content: props.content
    });

    useEffect(() => {
        if (validationResult) {
            setValidationResult(undefined);
        }
    }, [values.brandingIds, values.distributorId, values.versionTypeId, values.code]);

    useEffect(() => {
        if (trailersOnly) {
            setValue("versionTypeId", versionTypes[0].id, { shouldValidate: true });
        }
    }, [versionTypes]);

    const handleAddNewContentVersion = async (inputs: CreateContentVersionFormInputs) => {
        try {
            const newVersion = await createNewContentVersion(inputs);

            if (!newVersion) {
                return;
            }

            props.onSubmit(newVersion);
            reset();

            success("Content version created");
        } catch (e) {
            error("Failed to create content version");
        }
    };

    const handleCancelClick = () => {
        props.onCancel();
    };

    const toggleTrailerVersion = (
        _: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setValue("versionTypeId", "");
        setTrailersOnly(checked ?? false);
    };

    return (
        <Form onSubmit={handleSubmit(handleAddNewContentVersion)}>
            <FormFields>
                <FormDistributorSearchDropdown
                    control={control}
                    errors={errors}
                    rules={{ required: "Search and select distributor" }}
                    name="distributorId"
                    label="Distributor"
                    placeholder={"Distributor"}
                />
                <FormDropdown
                    options={versionTypes.map((item) => mapToDropdownOption(item))}
                    label="Version Type"
                    control={control}
                    name="versionTypeId"
                    errors={errors}
                    rules={{ required: "Search and select version type" }}
                    disabled={trailersOnly}
                    disableClear={trailersOnly}
                />
                {props.content.type === ContentReferenceType.TvSeries && (
                    <FormTextField label="Code" name="code" control={control} errors={errors} />
                )}
                <FormMultiselectDropdown
                    options={airlines?.map((item) => mapToDropdownOption(item)) ?? []}
                    control={control}
                    name="brandingIds"
                    errors={errors}
                    label="Brandings"
                />

                <Checkbox label="Trailer version" onChange={toggleTrailerVersion} testId="toggle-trailer-version" />

                <FormValidationSummary
                    validationResult={validationResult}
                    styles={{ root: { padding: 0, marginTop: 10 } }}
                />
            </FormFields>
            <FormActions
                isLoading={formState.isSubmitting || formState.isValidating}
                onCancel={handleCancelClick}
                submitDisabled={validationResult?.isValid === false}
            />
        </Form>
    );
};
